/**
 * Services API Service (TASK-107.8)
 *
 * API client functions for construction service management.
 */
import apiClient from './client';
import type { LineItem } from '@/types/estimate';

export interface ServiceVariant {
  id: number;
  name: string;
  condition_keywords: string[];
  created_at: string;
}

/**
 * A single node in the computation graph (chain).
 * Keyed by node name in `Chain.nodes_data`.
 */
export interface ChainNode {
  formula?: string;
  inputs?: Array<
    | string
    | { name?: string; variable?: string; type?: string; unit?: string; description?: string }
  >;
  output?: string;
  output_variable?: string;
  [key: string]: unknown;
}

export interface Chain {
  id: number;
  execution_order: string[];
  required_inputs: Array<string | { name?: string; type?: string; unit?: string }>;
  constants: Record<string, number | string | boolean>;
  output_name: string;
  output_unit: string;
  /** Computation graph: { nodeName: { formula, inputs, output } } */
  nodes_data?: Record<string, ChainNode>;
}

/**
 * A single pricelist position. Real shape is flexible — may carry
 * `name` or `work`, optional `variant`, plus `price`/`unit`/`type`.
 */
export interface PricingItem {
  type?: string;
  name?: string;
  work?: string;
  variant?: string;
  price?: number | string;
  unit?: string;
  [key: string]: unknown;
}

// ── Questionnaire service type (TASK-332) ────────────────────────────────
//
// A questionnaire service collects typed client parameters WITHOUT computing
// a price. `document` semantics: the collected value is a CONFIRMATION status
// («есть, согласился выслать на …»), not a file.

export type QuestionnaireFieldType = 'number' | 'text' | 'enum' | 'document';

/** One typed question of a questionnaire service (TASK-332). */
export interface QuestionnaireField {
  name: string;
  type: QuestionnaireFieldType;
  /** Optional unit — meaningful for `number` questions (e.g. «м²»). */
  unit?: string;
  /** Hint for the bot: how/why to ask. */
  description?: string;
  required?: boolean;
  /** Allowed values — only for `enum` questions. */
  options?: string[];
}

export type CalculationMethod =
  | 'calculator'
  | 'classifier'
  | 'pricelist'
  | 'reference'
  | 'questionnaire';

export interface Service {
  id: number;
  name: string;
  description: string;
  keywords: string[];
  parent: number | null;
  order: number;
  is_aggregator: boolean;
  is_manual?: boolean;
  calculation_method: CalculationMethod;
  variants: ServiceVariant[];
  chains: Chain[];
  children_count: number;
  /** Pricelist positions (only for pricelist services). */
  pricing_data?: PricingItem[] | null;
  /** Aggregation formula (only for aggregator services). */
  aggregation_formula?: string | null;
  /** Typed questions (only for questionnaire services, TASK-332). */
  questionnaire_fields?: QuestionnaireField[] | null;
  created_at: string;
  updated_at: string;
}

export interface CreateServiceData {
  name: string;
  description?: string;
  keywords?: string[];
  parent?: number | null;
  calculation_method?: CalculationMethod;
  is_aggregator?: boolean;
  pricing_data?: PricingItem[];
  /** Typed questions (only for questionnaire services, TASK-332). */
  questionnaire_fields?: QuestionnaireField[];
  required_inputs?: Array<{ name: string; unit: string; type: string }>;
  constants?: Record<string, number>;
  formula?: string;
  post_formula?: string;
  post_formula_inputs?: Array<{ name: string; unit: string; type: string }>;
}

// ── Manual tree-test contracts (TASK-260) ────────────────────────────────
//
// Deterministic manual calculation of a service subtree WITHOUT the LLM.
// The user hand-picks leaf nodes + inputs; the backend runs the same
// `ChainExecutor` engine the AI agent uses and returns line items + a per-node
// formula trace. Types live here (next to `servicesApi`/`Service`/`Chain`) and
// re-use `LineItem` from `@/types/estimate`.

/**
 * One picked node in a manual tree-test run.
 *  - calculator → `inputs` (numeric leaf params).
 *  - classifier → `variant` + optional `quantity`.
 *  - pricelist  → `items` (per-position work/variant/quantity).
 */
export interface TreeTestSelection {
  service_id: number;
  inputs?: Record<string, number>;
  variant?: string;
  quantity?: number;
  items?: Array<{ work: string; variant?: string; quantity: number }>;
}

/** Request body for `POST /api/v1/services/test-tree/`. */
export interface TreeTestRequest {
  selections: TreeTestSelection[];
  /** For SWITCH selectors: { "<service_id>": "<chosen branch>" }. */
  selector_choices?: Record<string, string>;
}

/** One node's computation trace (formula + intermediate values + result). */
export interface TreeTestTraceNode {
  service_id: number;
  service_name: string;
  node: string;
  formula: string;
  inputs: Record<string, unknown>;
  intermediate: Record<string, unknown>;
  result: number;
}

/** Result of a manual tree-test calculation. */
export interface TreeTestResult {
  line_items: LineItem[];
  subtotal: number;
  overhead: number;
  total: number;
  trace: TreeTestTraceNode[];
  warnings: string[];
}

/**
 * Persisted state of the last manual tree-test run (per-user localStorage).
 * `result` is null before the first «Рассчитать».
 */
export interface TreeTestStoredState {
  request: TreeTestRequest;
  result: TreeTestResult | null;
}

// ── Internal helpers (KAN-157) ───────────────────────────────────────────

/**
 * The "system variable" name the backend injects as the sole chain input
 * when a service's chain is built from a post-formula (pricelist/classifier)
 * rather than from explicit `required_inputs` (calculator). Classifier uses
 * a price-shaped variable, everything else (pricelist) a sum-shaped one.
 */
function resolveSysVar(calculationMethod?: CalculationMethod): string {
  return calculationMethod === 'classifier' ? 'базовая_цена' : 'базовая_сумма';
}

export const servicesApi = {
  /**
   * List all services.
   */
  list: async (): Promise<Service[]> => {
    const { data } = await apiClient.get<Service[]>('/api/v1/services/');
    return data;
  },

  /**
   * Get a single service by ID.
   */
  getById: async (id: number): Promise<Service> => {
    const { data } = await apiClient.get<Service>(`/api/v1/services/${id}/`);
    return data;
  },

  /**
   * Get the rendered LLM prompt block for a service (read-only).
   * Backend reuses the prompt-builder; used for classifier rendering.
   */
  getPromptBlock: async (id: number): Promise<{ prompt_block: string }> => {
    const { data } = await apiClient.get<{ prompt_block: string }>(
      `/api/v1/services/${id}/prompt_block/`
    );
    return data;
  },

  /**
   * Create a new service.
   */
  create: async (payload: CreateServiceData): Promise<Service> => {
    const { data } = await apiClient.post<Service>(
      '/api/v1/services/',
      payload
    );
    return data;
  },

  /**
   * Update an existing service.
   */
  update: async (
    id: number,
    payload: Partial<CreateServiceData>
  ): Promise<Service> => {
    const { data } = await apiClient.patch<Service>(
      `/api/v1/services/${id}/`,
      payload
    );
    return data;
  },

  /**
   * Delete a service.
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/services/${id}/`);
  },

  /**
   * Create a variant for a service.
   */
  createVariant: async (
    serviceId: number,
    payload: { name: string; condition_keywords?: string[] }
  ): Promise<ServiceVariant> => {
    const { data } = await apiClient.post<ServiceVariant>(
      `/api/v1/services/${serviceId}/variants/`,
      payload
    );
    return data;
  },

  /**
   * Delete a variant from a service.
   */
  deleteVariant: async (
    serviceId: number,
    variantId: number
  ): Promise<void> => {
    await apiClient.delete(
      `/api/v1/services/${serviceId}/variants/${variantId}/`
    );
  },

  /**
   * Create a chain for a service (formula → nodes_data conversion).
   */
  createChain: async (
    serviceId: number,
    payload: {
      formula: string;
      required_inputs?: Array<{ name: string; unit?: string; type?: string }>;
      constants?: Record<string, number>;
      output_name?: string;
      output_unit?: string;
    }
  ): Promise<Chain> => {
    const { data } = await apiClient.post<Chain>(
      `/api/v1/services/${serviceId}/chains/`,
      payload
    );
    return data;
  },

  /**
   * Update a chain for a service.
   */
  updateChain: async (
    serviceId: number,
    chainId: number,
    payload: {
      formula?: string;
      required_inputs?: Array<{ name: string; unit?: string; type?: string }>;
      constants?: Record<string, number>;
    }
  ): Promise<Chain> => {
    const { data } = await apiClient.patch<Chain>(
      `/api/v1/services/${serviceId}/chains/${chainId}/`,
      payload
    );
    return data;
  },

  /**
   * Move a service to a new parent and/or position.
   */
  move: async (
    id: number,
    payload: { parent: number | null; order: number }
  ): Promise<Service> => {
    const { data } = await apiClient.post<Service>(
      `/api/v1/services/${id}/move/`,
      payload
    );
    return data;
  },

  /**
   * Create a service AND (when the payload carries a formula) its chain, in
   * one orchestrated call (KAN-157 — relocated out of CreateServiceDialog's
   * `createMutation.mutationFn`).
   *
   * - `formula` (calculator): the chain's required_inputs/constants come
   *   from `payload.required_inputs`/`payload.constants` as-is.
   * - `post_formula` (pricelist/classifier): the chain gets a single
   *   synthetic required_input — the "system variable" the backend injects
   *   (`resolveSysVar`) — unit `руб`.
   * - Neither present: only `create()` runs, no chain is created.
   */
  createWithChain: async (payload: CreateServiceData): Promise<Service> => {
    const { formula: f, post_formula: pf, ...servicePayload } = payload;
    const created = await servicesApi.create(servicePayload);

    const formulaToSave = f || pf;
    if (formulaToSave) {
      const chainInputs: Array<{ name: string; unit?: string; type?: string }> = [];
      const chainConstants: Record<string, number> = {};

      if (f && payload.calculation_method === 'calculator') {
        if (payload.required_inputs) {
          chainInputs.push(...payload.required_inputs);
        }
        if (payload.constants) {
          Object.assign(chainConstants, payload.constants);
        }
      } else if (pf) {
        chainInputs.push({ name: resolveSysVar(payload.calculation_method), unit: 'руб', type: 'number' });
      }

      await servicesApi.createChain(created.id, {
        formula: formulaToSave,
        required_inputs: chainInputs.length > 0 ? chainInputs : undefined,
        constants: Object.keys(chainConstants).length > 0 ? chainConstants : undefined,
      });
    }

    return created;
  },

  /**
   * Update a service AND its chain (create/update as needed) AND move it to
   * a new parent, in one orchestrated call (KAN-157 — relocated out of
   * CreateServiceDialog's `updateMutation.mutationFn`).
   *
   * - Chain write picks `updateChain` when the updated service already has
   *   a chain (`updated.chains[0]`), else `createChain` — same
   *   required_inputs/constants derivation as `createWithChain`.
   * - `move()` fires only when `data.parent` is present AND differs from
   *   `originalParent` (so a no-op save never issues a pointless move).
   */
  updateWithChain: async (
    id: number,
    data: Partial<CreateServiceData>,
    originalParent?: number | null
  ): Promise<Service> => {
    const { formula: f, post_formula: pf, parent: newParent, ...serviceData } = data;
    const updated = await servicesApi.update(id, serviceData);

    const formulaToSave = f || pf;
    if (formulaToSave) {
      const chainInputs: Array<{ name: string; unit?: string; type?: string }> = [];
      const chainConstants: Record<string, number> = {};

      if (f && data.calculation_method === 'calculator') {
        if (data.required_inputs) {
          chainInputs.push(...data.required_inputs);
        }
        if (data.constants) {
          Object.assign(chainConstants, data.constants);
        }
      } else if (pf) {
        chainInputs.push({ name: resolveSysVar(data.calculation_method), unit: 'руб', type: 'number' });
      }

      const existingChain = updated.chains?.[0];
      if (existingChain) {
        await servicesApi.updateChain(updated.id, existingChain.id, {
          formula: formulaToSave,
          required_inputs: chainInputs.length > 0 ? chainInputs : undefined,
          constants: Object.keys(chainConstants).length > 0 ? chainConstants : undefined,
        });
      } else {
        await servicesApi.createChain(updated.id, {
          formula: formulaToSave,
          required_inputs: chainInputs.length > 0 ? chainInputs : undefined,
          constants: Object.keys(chainConstants).length > 0 ? chainConstants : undefined,
        });
      }
    }

    // Move to new parent if changed.
    if (newParent !== undefined && newParent !== originalParent) {
      await servicesApi.move(id, { parent: newParent ?? null, order: 0 });
    }

    return updated;
  },

  /**
   * Manual tree-test calculation (TASK-260) — deterministic, no LLM.
   *
   * Sends hand-picked nodes + inputs to the backend, which runs the same
   * `ChainExecutor` engine and returns line items + subtotal/overhead/total +
   * a per-node formula trace.
   */
  testTree: async (req: TreeTestRequest): Promise<TreeTestResult> => {
    const { data } = await apiClient.post<TreeTestResult>(
      '/api/v1/services/test-tree/',
      req
    );
    return data;
  },

  /**
   * Apply changes — regenerate prompts so the bot uses updated services.
   */
  applyChanges: async (): Promise<{
    status: string;
    message: string;
    users_updated?: number;
  }> => {
    const { data } = await apiClient.post('/api/v1/services/apply_changes/');
    return data;
  },
};

export default servicesApi;
